/**
 * Public Slurp Jackpot page.
 *
 * Three sections, top to bottom:
 *   1. Live pot counter — current jackpot accumulation, refreshed on
 *      mount + on a "Refresh" tap. No interval polling (see
 *      feedback_no_rpc_polling memory).
 *   2. How it works — three steps, plain English, no marketing fluff.
 *   3. Previous winners — empty until the first monthly draw lands.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Info, X } from "lucide-react";
import { slurpApi } from "../lib/slurpApi";

function JackpotHowItWorksModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="pm-modal__bg" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pm-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h3 className="pm-modal__title">How the monthly jackpot works</h3>
        <p className="pm-modal__lede">
          <b>One random winner. Every month.</b>{" "}
          The pot builds itself from trade fees, and any wallet that hits
          the buy threshold for the month is in the draw.
        </p>

        <h4 className="pm-modal__h4">Fees fund the pot</h4>
        <p>
          Every trade on every slurp token routes <b>12.5% of LP fees</b>{" "}
          into a single site-wide jackpot pot. Pool-wide, automatic,
          on-chain. Nobody has to opt in or claim.
        </p>

        <h4 className="pm-modal__h4">$100 in buys to enter</h4>
        <p>
          Spend at least <b>$100 in cumulative buys</b> across any slurp
          tokens during the month and your wallet is entered. Any mix
          of tokens counts. The entry is the buy itself, no signup or
          ticket purchase.
        </p>

        <h4 className="pm-modal__h4">One winner per month</h4>
        <p>
          At month-end, one random qualifying wallet is drawn. Full pot
          lands as SOL in the winner's wallet automatically.
        </p>

        <h4 className="pm-modal__h4">Why it's fair</h4>
        <ul className="pm-modal__list">
          <li><b>One wallet, one ticket.</b> Buying more doesn't buy more odds.</li>
          <li><b>Random draw</b> at month-end, no human picking the winner.</li>
          <li><b>Pot size is public</b> on-chain the whole month.</li>
          <li><b>Payout is automatic</b>, straight to the winner's wallet.</li>
        </ul>
      </div>
    </div>
  );
}

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  if (v >= 1)   return `$${v.toFixed(2)}`;
  // Sub-dollar amounts: keep enough decimals to actually see a value
  // (otherwise tiny test claims render as $0.00).
  if (v >= 0.01) return `$${v.toFixed(2)}`;
  if (v > 0)     return `$${v.toFixed(4)}`;
  return `$0.00`;
};
const fmtSol = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return `${v.toFixed(0)} SOL`;
  if (v >= 1)    return `${v.toFixed(2)} SOL`;
  return `${v.toFixed(4)} SOL`;
};
const shortWallet = (w) => (w ? `${w.slice(0, 4)}…${w.slice(-4)}` : "");
const fmtMonth = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

export default function JackpotPage() {
  const [pot, setPot] = useState(null);
  const [winners, setWinners] = useState(null);
  // Wallet → { username, image_url } | null. Populated after winners
  // load; null means "fetched and no profile exists" so we render the
  // short wallet instead.
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, w] = await Promise.all([
        slurpApi.getJackpotPot(),
        slurpApi.getJackpotWinners(),
      ]);
      setPot(p);
      const list = w?.winners ?? [];
      setWinners(list);
      // Fetch profiles for every winner in parallel. 404s mean "no
      // profile" — we silently treat those as null. Don't block UI on
      // the lookups; this runs after the main pot + winners render.
      const wallets = [...new Set(list.map((x) => x.winner_wallet))];
      Promise.allSettled(
        wallets.map((wallet) =>
          slurpApi.getProfile(wallet).then((r) => [wallet, r?.profile ?? null])
        )
      ).then((results) => {
        const next = {};
        for (const r of results) {
          if (r.status === "fulfilled") {
            const [wallet, profile] = r.value;
            next[wallet] = profile;
          }
        }
        setProfiles((prev) => ({ ...prev, ...next }));
      });
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // First draw is locked to July 31, 2026 UTC so the inaugural pot has
  // a full launch month + extra time to accumulate. After that, draws
  // fall on the last day of each calendar month UTC. All math in UTC
  // so users worldwide see the same deadline as the operator does.
  const FIRST_DRAW = new Date(Date.UTC(2026, 6, 31, 23, 59, 0));
  const nextDraw = (() => {
    const now = new Date();
    const eom = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 0));
    const target = eom < FIRST_DRAW ? FIRST_DRAW : eom;
    return target.toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
      timeZone: "UTC",
    });
  })();

  return (
    <div className="jp-page">
      <header className="jp-hero">
        <video
          className="jp-hero__video"
          src="/images/webp/jackpot.MP4"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="jp-hero__copy">
          <div className="jp-hero__eyebrow">
            <img
              src="/images/png/slurpface (1).png"
              alt=""
              className="jp-hero__face"
            />
            SLURP · MONTHLY JACKPOT
          </div>
          <h1 className="jp-hero__title">
            One winner.<br />
            <span className="pm-hero-line2">
              <span><span className="jp-hero__every">Every</span> month.</span>
              <button
                type="button"
                className="pm-hero-sticker pm-hero-sticker--desktop"
                onClick={() => setHowOpen(true)}
              >
                <Info size={14} /> See how it works
              </button>
            </span>
          </h1>
        </div>
        <div className="jp-hero__sub">
          <p>
            12.5% of LP fees on every slurp token piles into a site-wide
            pot. Spend $100 buying any slurp tokens this month and you're
            entered with an equal chance to win.
          </p>
          <button
            type="button"
            className="pm-hero-sticker pm-hero-sticker--mobile"
            onClick={() => setHowOpen(true)}
          >
            <Info size={14} /> See how it works
          </button>
        </div>
      </header>

      <section className="jp-counter">
        <div className="jp-counter__card">
          <div className="jp-counter__label">CURRENT POT</div>
          <div className="jp-counter__value">
            {loading && !pot
              ? <span className="jp-counter__skel">$—</span>
              : fmtUsd(pot?.pot_usd)}
          </div>
          <div className="jp-counter__sub">
            {fmtSol(pot?.pot_sol)} accumulated
          </div>
          <div className="jp-draw-sticker">
            <div className="jp-draw-sticker__label">NEXT DRAW</div>
            <div className="jp-draw-sticker__date">{nextDraw}</div>
          </div>
        </div>
      </section>

      {error && <div className="jp-error">{error}</div>}

      <section className="jp-section">
        <div className="jp-section__head">
          <div className="jp-section__title-block">
            <div className="jp-section__eyebrow">HALL OF FAME</div>
            <h2 className="jp-section__title">Previous winners</h2>
          </div>
        </div>
        {winners === null ? (
          <div className="jp-winners__empty">Loading…</div>
        ) : winners.filter((w) => w.paid_tx).length === 0 ? (
          <div className="jp-winners__empty">
            No winners yet. First draw on {nextDraw}.
          </div>
        ) : (
          <div className="jp-winners__list">
            {winners.filter((w) => w.paid_tx).map((w) => {
              const profile = profiles[w.winner_wallet];
              const display = profile?.username
                ? `@${profile.username}`
                : shortWallet(w.winner_wallet);
              return (
                <div key={w.id} className="jp-winners__card">
                  <div className="jp-winners__when">
                    {fmtMonth(w.drawn_at).toUpperCase()}
                  </div>
                  <div className="jp-winners__who">
                    {profile?.image_url ? (
                      <img
                        src={profile.image_url}
                        alt=""
                        className="jp-winners__avatar"
                      />
                    ) : (
                      <div className="jp-winners__avatar jp-winners__avatar--ph" />
                    )}
                    <div className="jp-winners__name-block">
                      <a
                        href={`https://solscan.io/account/${w.winner_wallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="jp-winners__name"
                      >
                        {display}
                      </a>
                      {profile?.username && (
                        <div className="jp-winners__addr">
                          {shortWallet(w.winner_wallet)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="jp-winners__prize">
                    <div className="jp-winners__usd">
                      {fmtUsd((w.amount_sol || 0) * (pot?.sol_usd || 0))}
                    </div>
                    <div className="jp-winners__sol">
                      {fmtSol(w.amount_sol)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {howOpen && <JackpotHowItWorksModal onClose={() => setHowOpen(false)} />}
    </div>
  );
}
